import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  Timestamp,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  writeBatch,
  increment,
  limit
} from 'firebase/firestore';
import { db } from '../firebase';
import { getGLAccountById, GLAccount, createDefaultAccountsReceivable } from './glAccountServices';

// GL Transaction interface
export interface GLTransaction {
  id?: string;
  transactionDate: Date | Timestamp;
  description: string;
  referenceType: string; // e.g., 'expense', 'income', 'journal'
  referenceId: string; // ID of the related document (expense, income, etc.)
  entries: GLTransactionEntry[];
  createdAt?: Date | Timestamp;
  updatedAt?: Date | Timestamp;
}

// GL Transaction Entry interface
export interface GLTransactionEntry {
  accountId: string;
  accountNumber: string;
  accountName: string;
  debit: number;
  credit: number;
}

// Create a GL transaction with multiple entries
// The deleteGLTransaction function is defined later in this file

export const createGLTransaction = async (transaction: Omit<GLTransaction, 'id'>): Promise<string> => {
  try {
    // Validate that debits = credits
    const totalDebits = transaction.entries.reduce((sum, entry) => sum + entry.debit, 0);
    const totalCredits = transaction.entries.reduce((sum, entry) => sum + entry.credit, 0);
    
    if (Math.abs(totalDebits - totalCredits) > 0.001) { // Allow for small floating point differences
      throw new Error('Transaction is not balanced. Total debits must equal total credits.');
    }
    
    // Create the transaction
    const transactionData = {
      ...transaction,
      transactionDate: transaction.transactionDate instanceof Date 
        ? Timestamp.fromDate(transaction.transactionDate) 
        : transaction.transactionDate,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const batch = writeBatch(db);
    
    // Add the transaction
    const transactionRef = doc(collection(db, 'glTransactions'));
    batch.set(transactionRef, transactionData);
    
    // Update account balances
    for (const entry of transaction.entries) {
      const accountRef = doc(db, 'glAccounts', entry.accountId);
      const account = await getGLAccountById(entry.accountId);
      
      if (!account) {
        throw new Error(`Account ${entry.accountId} not found`);
      }
      
      // For debit-normal accounts (Assets, Expenses):
      // - Debits increase the balance
      // - Credits decrease the balance
      // For credit-normal accounts (Liabilities, Equity, Revenue):
      // - Credits increase the balance
      // - Debits decrease the balance
      let balanceChange = 0;
      
      if (account.balanceType === 'debit') {
        balanceChange = entry.debit - entry.credit;
      } else {
        balanceChange = entry.credit - entry.debit;
      }
      
      batch.update(accountRef, {
        currentBalance: increment(balanceChange),
        updatedAt: serverTimestamp()
      });
    }
    
    // Commit all changes
    await batch.commit();
    
    return transactionRef.id;
  } catch (error) {
    console.error('Error creating GL transaction:', error);
    throw error;
  }
};

// Get GL transactions with optional filters
export const getGLTransactions = async (
  startDate?: Date | null,
  endDate?: Date | null,
  referenceType?: string,
  referenceId?: string
): Promise<GLTransaction[]> => {
  try {
    let transactionsQuery = query(
      collection(db, 'glTransactions'),
      orderBy('transactionDate', 'desc')
    );
    
    // Apply filters if provided
    if (startDate) {
      transactionsQuery = query(
        transactionsQuery,
        where('transactionDate', '>=', Timestamp.fromDate(startDate))
      );
    }
    
    if (endDate) {
      transactionsQuery = query(
        transactionsQuery,
        where('transactionDate', '<=', Timestamp.fromDate(endDate))
      );
    }
    
    if (referenceType) {
      transactionsQuery = query(
        transactionsQuery,
        where('referenceType', '==', referenceType)
      );
    }
    
    if (referenceId) {
      transactionsQuery = query(
        transactionsQuery,
        where('referenceId', '==', referenceId)
      );
    }
    
    const snapshot = await getDocs(transactionsQuery);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        transactionDate: data.transactionDate?.toDate() || new Date(),
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate()
      } as GLTransaction;
    });
  } catch (error) {
    console.error('Error getting GL transactions:', error);
    throw error;
  }
};

// Get a single GL transaction by ID
export const getGLTransactionById = async (id: string): Promise<GLTransaction | null> => {
  try {
    const transactionDoc = await getDoc(doc(db, 'glTransactions', id));
    
    if (transactionDoc.exists()) {
      const data = transactionDoc.data();
      return {
        id: transactionDoc.id,
        ...data,
        transactionDate: data.transactionDate?.toDate() || new Date(),
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate()
      } as GLTransaction;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting GL transaction:', error);
    throw error;
  }
};

// Delete a GL transaction and reverse its effects on account balances
export const deleteGLTransaction = async (id: string): Promise<void> => {
  try {
    // First verify the transaction exists
    const transactionRef = doc(db, 'glTransactions', id);
    const transactionDoc = await getDoc(transactionRef);
    
    if (!transactionDoc.exists()) {
      console.warn(`Transaction with ID ${id} not found, nothing to delete`);
      return; // Exit early if transaction doesn't exist
    }
    
    const transaction = {
      id,
      ...transactionDoc.data()
    } as GLTransaction;
    
    // Use a batch to ensure atomicity
    const batch = writeBatch(db);
    
    // Delete the transaction
    batch.delete(transactionRef);
    
    console.log(`Reversing effects of transaction ${id} with ${transaction.entries?.length || 0} entries`);
    
    // Reverse the effects on account balances
    if (transaction.entries && transaction.entries.length > 0) {
      for (const entry of transaction.entries) {
        if (!entry.accountNumber) {
          console.warn('Entry missing account number, skipping balance update');
          continue;
        }
        
        // Find the account by account number instead of ID
        const accountsQuery = query(
          collection(db, 'glAccounts'),
          where('accountNumber', '==', entry.accountNumber),
          limit(1)
        );
        
        const accountSnapshot = await getDocs(accountsQuery);
        
        if (accountSnapshot.empty) {
          console.warn(`Account with number ${entry.accountNumber} not found, skipping balance update`);
          continue;
        }
        
        const accountDoc = accountSnapshot.docs[0];
        const account = {
          id: accountDoc.id,
          ...accountDoc.data()
        } as GLAccount;
        
        let balanceChange = 0;
        
        if (account.balanceType === 'debit') {
          balanceChange = entry.credit - entry.debit; // Reverse of the original
        } else {
          balanceChange = entry.debit - entry.credit; // Reverse of the original
        }
        
        console.log(`Updating account ${account.accountNumber} balance by ${balanceChange}`);
        
        batch.update(accountDoc.ref, {
          currentBalance: increment(balanceChange),
          updatedAt: serverTimestamp()
        });
      }
    }
    
    // Commit all changes
    console.log(`Committing batch for transaction deletion ${id}`);
    await batch.commit();
    console.log(`Successfully deleted GL transaction ${id}`);
  } catch (error) {
    console.error('Error deleting GL transaction:', error);
    throw error;
  }
};

// Get GL transactions by account ID
export const getGLTransactionsByAccountId = async (
  accountId: string,
  startDate?: Date | null,
  endDate?: Date | null,
  includeFullTransactions?: boolean,
  limit?: number
): Promise<GLTransaction[]> => {
  try {
    const transactionsQuery = query(
      collection(db, 'glTransactions'),
      orderBy('transactionDate', 'desc')
    );
    
    // Get all transactions first (we'll filter by account ID later)
    let queryWithDates = transactionsQuery;
    
    if (startDate) {
      queryWithDates = query(
        queryWithDates,
        where('transactionDate', '>=', Timestamp.fromDate(startDate))
      );
    }
    
    if (endDate) {
      queryWithDates = query(
        queryWithDates,
        where('transactionDate', '<=', Timestamp.fromDate(endDate))
      );
    }
    
    const snapshot = await getDocs(queryWithDates);
    
    // Map all transactions
    const allTransactions = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        transactionDate: data.transactionDate?.toDate() || new Date(),
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate()
      } as GLTransaction;
    });
    
    // Filter transactions that contain entries for the specified account ID
    const transactionIds = new Set<string>();
    
    // First, identify all transaction IDs that involve this account
    allTransactions.forEach(transaction => {
      if (transaction.entries.some(entry => entry.accountId === accountId)) {
        transactionIds.add(transaction.id || '');
      }
    });
    
    // Then, either return full transactions or just the filtered entries
    const filteredTransactions = allTransactions
      .filter(transaction => transactionIds.has(transaction.id || ''))
      .map(transaction => ({
        ...transaction,
        // If includeFullTransactions is true, keep all entries, otherwise filter to just this account
        entries: includeFullTransactions 
          ? transaction.entries 
          : transaction.entries.filter(entry => entry.accountId === accountId)
      }));
    
    // Apply limit if specified
    if (limit && limit > 0) {
      return filteredTransactions.slice(0, limit);
    }
    
    return filteredTransactions;
  } catch (error) {
    console.error('Error getting GL transactions by account ID:', error);
    throw error;
  }
};

// Get all bank accounts (1100000 series)
export const getBankGLAccounts = async (): Promise<GLAccount[]> => {
  try {
    const bankAccountsQuery = query(
      collection(db, 'glAccounts'),
      where('accountNumber', '>=', '1100000'),
      where('accountNumber', '<', '1200000'),
      where('level', '==', 3), // Only get detail accounts
      where('isActive', '==', true),
      orderBy('accountNumber', 'asc')
    );
    
    const snapshot = await getDocs(bankAccountsQuery);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as GLAccount));
  } catch (error) {
    console.error('Error getting bank GL accounts:', error);
    throw error;
  }
};

// Get all expense GL accounts (7000000 series)
export const getExpenseGLAccounts = async (): Promise<GLAccount[]> => {
  try {
    const expenseAccountsQuery = query(
      collection(db, 'glAccounts'),
      where('accountNumber', '>=', '7000000'),
      where('accountNumber', '<', '8000000'),
      where('level', '==', 3), // Only get detail accounts
      where('isActive', '==', true),
      orderBy('accountNumber', 'asc')
    );
    
    const snapshot = await getDocs(expenseAccountsQuery);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as GLAccount));
  } catch (error) {
    console.error('Error getting expense GL accounts:', error);
    throw error;
  }
};

// Get all revenue GL accounts (6000000 series)
export const getRevenueGLAccounts = async (): Promise<GLAccount[]> => {
  try {
    const accountsQuery = query(
      collection(db, 'glAccounts'),
      where('accountNumber', '>=', '6000000'),
      where('accountNumber', '<', '7000000'),
      orderBy('accountNumber')
    );
    
    const snapshot = await getDocs(accountsQuery);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as GLAccount));
  } catch (error) {
    console.error('Error getting revenue GL accounts:', error);
    throw error;
  }
};

// Get all accounts receivable GL accounts (1200000 series)
export const getAccountsReceivableGLAccounts = async (): Promise<GLAccount[]> => {
  try {
    const accountsQuery = query(
      collection(db, 'glAccounts'),
      where('accountNumber', '>=', '1200000'),
      where('accountNumber', '<', '1300000'),
      orderBy('accountNumber')
    );
    
    const snapshot = await getDocs(accountsQuery);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as GLAccount));
  } catch (error) {
    console.error('Error getting accounts receivable GL accounts:', error);
    throw error;
  }
};

// Generate GL entries for an expense
export const generateExpenseGLEntries = async (
  expenseAmount: number,
  expenseDate: Date | Timestamp,
  expenseId: string,
  expenseTitle: string,
  expenseCategory: string,
  paymentMethod: string = 'Bank Transfer' // Default to Bank Transfer if not specified
): Promise<string> => {
  try {
    // Get all expense GL accounts
    const expenseGLAccounts = await getExpenseGLAccounts();
    
    // Find the matching GL account by name (exact match)
    let expenseAccount = expenseGLAccounts.find(account => 
      account.accountName.toLowerCase() === expenseCategory.toLowerCase());
    
    // If no exact match, try to find a similar account (contains the category name)
    if (!expenseAccount) {
      expenseAccount = expenseGLAccounts.find(account => 
        account.accountName.toLowerCase().includes(expenseCategory.toLowerCase()));
    }
    
    // If still no match, use the first expense account as fallback
    if (!expenseAccount && expenseGLAccounts.length > 0) {
      console.warn(`No specific GL account found for ${expenseCategory}. Using the first available expense account.`);
      expenseAccount = expenseGLAccounts[0];
    }
    
    // If no expense accounts at all, throw an error
    if (!expenseAccount) {
      throw new Error(`No expense GL accounts found. Please set up GL accounts starting with 7000000 first.`);
    }
    
    // Ensure expense account has all required fields with proper types
    const validatedExpenseAccount: GLAccount = {
      id: expenseAccount.id,
      accountNumber: typeof expenseAccount.accountNumber === 'string' ? expenseAccount.accountNumber : '',
      accountName: typeof expenseAccount.accountName === 'string' ? expenseAccount.accountName : '',
      accountType: typeof expenseAccount.accountType === 'string' ? expenseAccount.accountType : 'Expenses',
      balanceType: (expenseAccount.balanceType === 'debit' || expenseAccount.balanceType === 'credit') ? 
        expenseAccount.balanceType : 'debit',
      isActive: typeof expenseAccount.isActive === 'boolean' ? expenseAccount.isActive : true,
      level: typeof expenseAccount.level === 'number' ? expenseAccount.level : 3,
      currentBalance: typeof expenseAccount.currentBalance === 'number' ? expenseAccount.currentBalance : 0,
      parentAccount: typeof expenseAccount.parentAccount === 'string' ? expenseAccount.parentAccount : '',
      createdAt: expenseAccount.createdAt || new Date(),
      updatedAt: expenseAccount.updatedAt || new Date()
    };
    
    // Find the specific bank account based on payment method from the 1100000 series
    // Map payment methods to account name patterns
    let accountNamePattern: string;
    
    switch (paymentMethod.toLowerCase()) {
      case 'bank transfer':
      case 'direct debit':
        accountNamePattern = 'MAYBANK'; // For bank transfers, use MAYBANK
        break;
      case 'credit card':
        accountNamePattern = 'CIMB'; // For credit card payments, use CIMB
        break;
      case 'cash':
        accountNamePattern = 'Cash'; // For cash payments
        break;
      default:
        accountNamePattern = 'MAYBANK'; // Default to MAYBANK for other methods
    }
    
    // Query for bank accounts in the 1100000 series
    const bankAccountQuery = query(
      collection(db, 'glAccounts'),
      where('accountNumber', '>=', '1100000'),
      where('accountNumber', '<', '1200000'),
      where('isActive', '==', true)
    );
    
    const bankSnapshot = await getDocs(bankAccountQuery);
    
    if (bankSnapshot.empty) {
      throw new Error('No bank accounts found in the 1100000 series. Please set up GL accounts first.');
    }
    
    // Find the matching bank account based on the payment method
    let bankAccount = bankSnapshot.docs.find(doc => 
      doc.data().accountName.toUpperCase().includes(accountNamePattern.toUpperCase())
    );
    
    // If no specific match found, use the first bank account as fallback
    if (!bankAccount) {
      console.warn(`No specific bank account found for ${paymentMethod}. Using the first available bank account.`);
      bankAccount = bankSnapshot.docs[0];
    }
    
    // Get the bank account data and ensure all fields match the GLAccount interface
    const bankAccountData = bankAccount.data();
    
    // Create a properly typed GLAccount object
    const cashAccount: GLAccount = {
      id: bankAccount.id,
      accountNumber: typeof bankAccountData.accountNumber === 'string' ? bankAccountData.accountNumber : '',
      accountName: typeof bankAccountData.accountName === 'string' ? bankAccountData.accountName : '',
      accountType: typeof bankAccountData.accountType === 'string' ? bankAccountData.accountType : 'Assets',
      balanceType: (bankAccountData.balanceType === 'debit' || bankAccountData.balanceType === 'credit') ? 
        bankAccountData.balanceType : 'debit',
      currentBalance: typeof bankAccountData.currentBalance === 'number' ? bankAccountData.currentBalance : 0,
      isActive: typeof bankAccountData.isActive === 'boolean' ? bankAccountData.isActive : true,
      level: typeof bankAccountData.level === 'number' ? bankAccountData.level : 3,
      parentAccount: typeof bankAccountData.parentAccount === 'string' ? bankAccountData.parentAccount : '',
      createdAt: bankAccountData.createdAt || new Date(),
      updatedAt: bankAccountData.updatedAt || new Date()
    };
    
    // Create the GL transaction
    const transaction: Omit<GLTransaction, 'id'> = {
      transactionDate: expenseDate,
      description: `Expense: ${expenseTitle}`,
      referenceType: 'expense',
      referenceId: expenseId,
      entries: [
        {
          accountId: validatedExpenseAccount.id || '',
          accountNumber: validatedExpenseAccount.accountNumber,
          accountName: validatedExpenseAccount.accountName,
          debit: expenseAmount,
          credit: 0
        },
        {
          accountId: cashAccount.id || '',
          accountNumber: cashAccount.accountNumber,
          accountName: cashAccount.accountName,
          debit: 0,
          credit: expenseAmount
        }
      ]
    };
    
    return await createGLTransaction(transaction);
  } catch (error) {
    console.error('Error generating expense GL entries:', error);
    throw error;
  }
};

// Generate GL entries for an invoice
export const generateInvoiceGLEntries = async (
  invoice: {
    id?: string;
    invoiceNumber: string;
    customerName: string;
    issueDate: Date | Timestamp;
    totalAmount: number;
    items: Array<{
      description: string;
      quantity: number;
      unitPrice: number;
      glAccountId: string;
      total: number;
    }>;
  }
): Promise<string> => {
  try {
    // Find the specific AR sub-account (1200001 - General-Accounts Receivable)
    let arAccount: GLAccount;
    const arSubQuery = query(
      collection(db, 'glAccounts'),
      where('accountNumber', '==', '1200001')
    );
    
    const arSubSnapshot = await getDocs(arSubQuery);
    
    if (!arSubSnapshot.empty) {
      arAccount = { id: arSubSnapshot.docs[0].id, ...arSubSnapshot.docs[0].data() } as GLAccount;
    } else {
      // If the specific sub-account doesn't exist, try to find any AR account
      const arAccounts = await getAccountsReceivableGLAccounts();
      
      if (arAccounts.length === 0) {
        // Try to create the default AR accounts
        await createDefaultAccountsReceivable();
        
        // Try to get the newly created AR sub-account
        const newArSubSnapshot = await getDocs(arSubQuery);
        
        if (newArSubSnapshot.empty) {
          throw new Error('Failed to create or find Accounts Receivable sub-account. Please create account 1200001 manually.');
        }
        
        arAccount = { id: newArSubSnapshot.docs[0].id, ...newArSubSnapshot.docs[0].data() } as GLAccount;
      } else {
        throw new Error('Accounts Receivable main account exists but sub-account 1200001 is missing. Please run Admin Utilities to create the proper account structure.');
      }
    }

    // Get the revenue clearing account (6969999)
    const revClearingQuery = query(
      collection(db, 'glAccounts'),
      where('accountNumber', '==', '6969999')
    );
    
    const revClearingSnapshot = await getDocs(revClearingQuery);
    
    if (revClearingSnapshot.empty) {
      throw new Error('Revenue clearing account (6969999) not found. Please run the admin utilities to create clearing accounts.');
    }
    
    const revClearingAccount = { id: revClearingSnapshot.docs[0].id, ...revClearingSnapshot.docs[0].data() } as GLAccount;

    // Prepare GL entries
    const entries: GLTransactionEntry[] = [];

    // Add the debit entry to Accounts Receivable
    entries.push({
      accountId: arAccount.id || '',
      accountNumber: arAccount.accountNumber,
      accountName: arAccount.accountName,
      debit: invoice.totalAmount,
      credit: 0
    });
    
    // Add credit entry to Revenue Clearing Account
    entries.push({
      accountId: revClearingAccount.id || '',
      accountNumber: revClearingAccount.accountNumber,
      accountName: revClearingAccount.accountName,
      debit: 0,
      credit: invoice.totalAmount
    });
    
    // Add debit entries to Revenue Clearing Account and credit entries to Revenue accounts
    // for each revenue item (double-entry accounting)
    for (const item of invoice.items) {
      if (!item.glAccountId) {
        throw new Error('GL Account ID is required for each invoice item');
      }

      const glAccount = await getGLAccountById(item.glAccountId);
      if (!glAccount) {
        throw new Error(`GL Account ${item.glAccountId} not found`);
      }
      
      // Debit Revenue Clearing Account
      entries.push({
        accountId: revClearingAccount.id || '',
        accountNumber: revClearingAccount.accountNumber,
        accountName: revClearingAccount.accountName,
        debit: item.total,
        credit: 0
      });
      
      // Credit Revenue Account
      entries.push({
        accountId: item.glAccountId,
        accountNumber: glAccount.accountNumber,
        accountName: glAccount.accountName,
        debit: 0,
        credit: item.total
      });
    }

    // Create the GL transaction
    const glTransaction: Omit<GLTransaction, 'id'> = {
      transactionDate: invoice.issueDate,
      description: `Invoice ${invoice.invoiceNumber} - ${invoice.customerName}`,
      referenceType: 'invoice',
      referenceId: invoice.id || '',
      entries: entries
    };

    // Create the transaction in the database
    return await createGLTransaction(glTransaction);
  } catch (error) {
    console.error('Error generating invoice GL entries:', error);
    throw error;
  }
};
